import React, { ErrorInfo, useCallback, useEffect, useMemo, useState } from "react";
import type { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge";
import { CACHE_POSTFIX } from "@swiggy-private/aatm-nirbhar-build/dist/constants/htmlCaching";
import { ErrorPage } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/ErrorPage";
import GlobalStyled from "@swiggy-private/aatm-nirbhar-ui/dist/lib/GlobalStyled";
import { NewRelicErrorBoundary } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/NewRelic";
import Blaze from "@swiggy-private/blaze.js";
import { FontStyle } from "@swiggy-private/dls-web-theme/dist/components/Fonts";
import {
    getAnalyticsConfigFromHeaders,
    getAnalyticsEvents,
    AnalyticsContext,
} from "@swiggy-private/portal-dineout-context";
import {
    SwiggyContextHeader,
    ApiProvider,
    DelayedActionToastProvider,
    ThemeProvider,
    DigitalMenuConfigProvider,
    ResponseContextProvider,
} from "@swiggy-private/portal-dineout-context";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import { isDevelopment } from "utils";
import { getBridgeSdk } from "utils/bridgeSdk";
import { sendCustomNREvViaBlaze } from "utils/newrelic";
import AppContentWrapper from "../components/AppContentWrapper";
import { DelayedActionToastRenderer } from "../components/DelayedActionToast";
import { DigitalMenuConfigLoader } from "../components/DigitalMenuConfigLoader";
import { NEW_RELIC_APPLICATION_ID, RECORD_PERCIEVED_TIME_EVENTS } from "../constants";
import { useBootstrap, sendErrorBoundaryEvent } from "../hooks/useBootstrap";
import { useEffectOnce } from "../hooks/useEffectOnce";
import { useExternalOrder } from "../hooks/useExternalOrder";
import { GlobalStyledDigitalMenu, ShuttleAppName } from "../styles/home";
import { installGlobalJSBridgeHandler } from "../utils/globalJsBridgeDispatcher";
import { closeWebview, getSwiggyHeaders } from "../utils/nativeBridgeHelper";

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
    getLayout?: (page: React.ReactElement) => React.ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps, router }: AppPropsWithLayout) {
    // Detect if the page is a cached version
    const isCached = router.route.includes(CACHE_POSTFIX);
    useBootstrap("portal_dineout", isCached);
    const { confirmOrderOnLaunch } = useExternalOrder();

    const [swiggyHeaders, setSwiggyHeaders] = useState<GetSwiggyHeadersResponsePayload>();

    useEffect(() => {
        async function initApp() {
            if (!router.isReady) {
                return;
            }
            try {
                const headers = await getSwiggyHeaders(true);
                setSwiggyHeaders(headers);
            } catch (_) {
                // no-op
            }
        }
        initApp();
    }, [router.isReady, router.query]);

    const onError = useCallback((error?: Error, errorInfo?: ErrorInfo) => {
        sendErrorBoundaryEvent(error, errorInfo);
    }, []);

    const getLayout = Component.getLayout ?? ((page) => page);
    const analyticsConfig = getAnalyticsConfigFromHeaders(swiggyHeaders, isDevelopment());
    const analyticsContextValue = getAnalyticsEvents(analyticsConfig);

    useEffect(() => {
        Blaze.recordStart(RECORD_PERCIEVED_TIME_EVENTS.APP_START);
    }, []);

    // Wire up the shared globalJSBridgeHandler dispatcher once per app load.
    // Feature hooks (Razorpay, Live Notifications, ...) register handlers
    // against this single global slot.
    useEffectOnce(() => {
        installGlobalJSBridgeHandler();
    });

    const absoluteForwardPath = useMemo(() => `/dineout${router.asPath}`, [router.asPath]);

    useEffect(() => {
        confirmOrderOnLaunch();
    }, [confirmOrderOnLaunch]);

    return (
        <NewRelicErrorBoundary
            applicationID={NEW_RELIC_APPLICATION_ID}
            onErrorCapture={onError}
            ErrorComponent={() => <ErrorPage onCtaClick={closeWebview} />}
        >
            <DigitalMenuConfigProvider>
                <ThemeProvider defaultTheme="dark" persistTheme={true} swiggyHeaders={swiggyHeaders}>
                    <AppContentWrapper swiggyHeaders={swiggyHeaders}>
                        <ShuttleAppName data-app-name={`${process.env.NEXT_PUBLIC_APP_NAME}`} />
                        <GlobalStyled />
                        <GlobalStyledDigitalMenu />
                        <FontStyle />

                        <SwiggyContextHeader.Provider
                            value={{
                                swiggyHeaders,
                                setSwiggyHeaders,
                            }}
                        >
                            <ApiProvider
                                sendEvent={sendCustomNREvViaBlaze}
                                sdk={getBridgeSdk()}
                                forwardPath={absoluteForwardPath}
                            >
                                <ResponseContextProvider>
                                    <DigitalMenuConfigLoader>
                                        <DelayedActionToastProvider>
                                            <AnalyticsContext.Provider value={analyticsContextValue}>
                                                {getLayout(<Component {...pageProps} />)}
                                            </AnalyticsContext.Provider>
                                            <DelayedActionToastRenderer />
                                        </DelayedActionToastProvider>
                                    </DigitalMenuConfigLoader>
                                </ResponseContextProvider>
                            </ApiProvider>
                        </SwiggyContextHeader.Provider>
                    </AppContentWrapper>
                </ThemeProvider>
            </DigitalMenuConfigProvider>
        </NewRelicErrorBoundary>
    );
}

export default MyApp;
